import type { Ref } from 'vue'

import {
  queueWorkflowKnowledgeAudioSelections,
  queueWorkflowKnowledgeConsistencyAssetImageSelections,
  queueWorkflowKnowledgeContexts,
  queueWorkflowKnowledgeImageSelections,
} from '@/utils/workflowKnowledge'

import {
  getMaterialDisplayDescription,
  getMaterialDisplayTitle,
} from './materialLibrary'
import type {
  AssetTab,
  ContentLibraryEntry,
  MaterialLibraryEntry,
  QueryResult,
} from './types'

interface UseKnowledgeBaseWorkflowBridgeOptions {
  activeTab: Ref<AssetTab>
  linkedProjectId: Ref<number | null>
  linkedSceneNumber: Ref<number | null>
  linkedAssetType: Ref<'character' | 'scene' | 'prop' | null>
  linkedAssetId: Ref<string | null>
  linkedAssetTypeLabel: Ref<string>
  linkedAudioRole: Ref<'narration' | 'bgm' | null>
  returnToWorkspace: () => void
  buildLinkedResultTitle: (result: QueryResult) => string
  success: (message: string) => void
}

export function useKnowledgeBaseWorkflowBridge(options: UseKnowledgeBaseWorkflowBridgeOptions) {
  function queueAssetContext(item: { title: string; content: string; source: string; metadata?: Record<string, any> | null }) {
    if (!options.linkedProjectId.value) {
      return false
    }

    queueWorkflowKnowledgeContexts(options.linkedProjectId.value, [
      {
        title: item.title,
        content: item.content,
        source: item.source,
        metadata: item.metadata || null,
      },
    ])
    return true
  }

  function linkResultToWorkflow(result: QueryResult) {
    if (!queueAssetContext({
      title: options.buildLinkedResultTitle(result),
      content: result.content,
      source: result.source,
      metadata: result.metadata || null,
    })) {
      return
    }

    options.success('已加入当前项目')
  }

  function handleUseResultAsSceneImage(result: QueryResult) {
    if (!options.linkedProjectId.value || !options.linkedSceneNumber.value || !result.metadata?.image_url) {
      return
    }

    queueWorkflowKnowledgeContexts(options.linkedProjectId.value, [
      {
        title: options.buildLinkedResultTitle(result),
        content: result.content,
        source: result.source,
        metadata: result.metadata || null,
      },
    ])
    queueWorkflowKnowledgeImageSelections(options.linkedProjectId.value, [
      {
        sceneNumber: options.linkedSceneNumber.value,
        title: options.buildLinkedResultTitle(result),
        image_url: result.metadata.image_url,
        source: result.source,
        content: result.content,
        metadata: result.metadata || null,
      },
    ])

    options.success(`已为第 ${options.linkedSceneNumber.value} 个分镜选好参考图`)
    options.returnToWorkspace()
  }

  function handleUseResultAsConsistencyAssetImage(result: QueryResult) {
    if (!options.linkedProjectId.value || !options.linkedAssetType.value || !options.linkedAssetId.value || !result.metadata?.image_url) {
      return
    }

    queueWorkflowKnowledgeContexts(options.linkedProjectId.value, [
      {
        title: options.buildLinkedResultTitle(result),
        content: result.content,
        source: result.source,
        metadata: result.metadata || null,
      },
    ])
    queueWorkflowKnowledgeConsistencyAssetImageSelections(options.linkedProjectId.value, [
      {
        assetType: options.linkedAssetType.value,
        assetId: options.linkedAssetId.value,
        title: options.buildLinkedResultTitle(result),
        image_url: result.metadata.image_url,
        source: result.source,
        content: result.content,
        metadata: result.metadata || null,
      },
    ])

    options.success(`已为当前${options.linkedAssetTypeLabel.value}选好官方参考图`)
    options.returnToWorkspace()
  }

  function linkContentEntryToWorkflow(entry: ContentLibraryEntry) {
    if (!queueAssetContext({
      title: `${entry.content_type} 素材`,
      content: entry.content,
      source: 'content_library',
      metadata: {
        project_title: '素材参考中心',
        title: `${entry.content_type} 素材`,
      },
    })) {
      return
    }

    options.success('素材内容已加入当前项目')
  }

  function linkMaterialToWorkflow(entry: MaterialLibraryEntry) {
    if (!queueAssetContext({
      title: getMaterialDisplayTitle(entry),
      content: getMaterialDisplayDescription(entry),
      source: 'material',
      metadata: {
        project_title: entry.project_title,
        title: getMaterialDisplayTitle(entry),
        image_url: entry.image_url,
      },
    })) {
      return
    }

    options.success(`${options.activeTab.value === 'audio' ? '音频' : options.activeTab.value === 'video' ? '视频' : '图片'}素材已加入当前项目`)
  }

  function handleUseMaterialAsFinalAudio(entry: MaterialLibraryEntry) {
    if (!options.linkedProjectId.value || !options.linkedAudioRole.value || !entry.image_url) {
      return
    }

    queueWorkflowKnowledgeContexts(options.linkedProjectId.value, [
      {
        title: getMaterialDisplayTitle(entry),
        content: getMaterialDisplayDescription(entry),
        source: 'material',
        metadata: {
          material_id: entry.id,
          project_title: entry.project_title,
          title: getMaterialDisplayTitle(entry),
          audio_url: entry.image_url,
        },
      },
    ])
    queueWorkflowKnowledgeAudioSelections(options.linkedProjectId.value, [
      {
        role: options.linkedAudioRole.value,
        material_id: entry.id,
        name: getMaterialDisplayTitle(entry),
        url: entry.image_url,
        source: 'material',
        duration_seconds: entry.duration ?? null,
        metadata: {
          material_id: entry.id,
          project_title: entry.project_title,
          title: getMaterialDisplayTitle(entry),
        },
      },
    ])

    options.success(`已设为当前${options.linkedAudioRole.value === 'narration' ? '旁白' : 'BGM'}素材`)
    options.returnToWorkspace()
  }

  function handleUseMaterialAsSceneImage(entry: MaterialLibraryEntry) {
    if (!options.linkedProjectId.value || !options.linkedSceneNumber.value) {
      return
    }

    queueWorkflowKnowledgeContexts(options.linkedProjectId.value, [
      {
        title: getMaterialDisplayTitle(entry),
        content: getMaterialDisplayDescription(entry),
        source: 'material',
        metadata: {
          project_title: entry.project_title,
          title: getMaterialDisplayTitle(entry),
          image_url: entry.image_url,
        },
      },
    ])
    queueWorkflowKnowledgeImageSelections(options.linkedProjectId.value, [
      {
        sceneNumber: options.linkedSceneNumber.value,
        title: getMaterialDisplayTitle(entry),
        image_url: entry.image_url,
        source: 'material',
        content: getMaterialDisplayDescription(entry),
        metadata: {
          project_title: entry.project_title,
          title: getMaterialDisplayTitle(entry),
          image_url: entry.image_url,
        },
      },
    ])

    options.success(`已为第 ${options.linkedSceneNumber.value} 个分镜选好图片`)
    options.returnToWorkspace()
  }

  function handleUseMaterialAsConsistencyAssetImage(entry: MaterialLibraryEntry) {
    if (!options.linkedProjectId.value || !options.linkedAssetType.value || !options.linkedAssetId.value) {
      return
    }

    queueWorkflowKnowledgeContexts(options.linkedProjectId.value, [
      {
        title: getMaterialDisplayTitle(entry),
        content: getMaterialDisplayDescription(entry),
        source: 'material',
        metadata: {
          project_title: entry.project_title,
          title: getMaterialDisplayTitle(entry),
          image_url: entry.image_url,
        },
      },
    ])
    queueWorkflowKnowledgeConsistencyAssetImageSelections(options.linkedProjectId.value, [
      {
        assetType: options.linkedAssetType.value,
        assetId: options.linkedAssetId.value,
        material_id: entry.id,
        title: getMaterialDisplayTitle(entry),
        image_url: entry.image_url,
        source: 'material',
        content: getMaterialDisplayDescription(entry),
        metadata: {
          material_id: entry.id,
          project_title: entry.project_title,
          title: getMaterialDisplayTitle(entry),
          image_url: entry.image_url,
        },
      },
    ])

    options.success(`已为当前${options.linkedAssetTypeLabel.value}选好官方参考图`)
    options.returnToWorkspace()
  }

  return {
    handleUseMaterialAsConsistencyAssetImage,
    handleUseMaterialAsFinalAudio,
    handleUseMaterialAsSceneImage,
    handleUseResultAsConsistencyAssetImage,
    handleUseResultAsSceneImage,
    linkContentEntryToWorkflow,
    linkMaterialToWorkflow,
    linkResultToWorkflow,
  }
}
